import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';
import PostCard from 'components/PostCard';

import Alert from 'primitives/Alert';
import Button from 'primitives/Button';
import EmptyState from 'primitives/EmptyState';
import LoadingIndicator from 'primitives/LoadingIndicator';

import CreatePostDialogButton from './CreatePostDialogButton';
import PostSlideOver from './PostSlideOver';

const POSTS_QUERY = gql(`
  query SyndicatePostsQuery($syndicateId: ID!, $cursor: ID, $limit: Int) {
    syndicate(id: $syndicateId) {
      id
      posts(cursor: $cursor, limit: $limit) {
        nodes {
          id
          status
          title
          message
          createdAt
          publishedAt
          deal {
            companyInformation {
              company {
                name
              }
            }
          }
          sender {
            id  
            name
          }
          syndicate {
            id
            name
            image
          }
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const SyndicatePosts: FC = () => {
  const [postOnDisplay, setPostOnDisplay] = useState('');

  const { syndicateId } = useParams<{ syndicateId: string }>() as {
    syndicateId: string;
  };

  const { loading, error, data, refetch, fetchMore } = useQuery(POSTS_QUERY, {
    variables: {
      syndicateId,
      limit: 15,
    },
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data || !data.syndicate.posts)
      return <ErrorMessage error={error} refetch={refetch} />;

    const syndicate = data.syndicate;
    const posts = syndicate.posts.nodes;
    const pageInfo = syndicate.posts.pageInfo;

    if (posts.length === 0)
      return (
        <EmptyState
          title="No posts received"
          description="Updates and information from Syndicates will appear here"
        />
      );

    return (
      <>
        <PostSlideOver
          open={!!postOnDisplay}
          postId={postOnDisplay}
          onClose={() => setPostOnDisplay('')}
        />
        <div className="flex flex-col">
          <div className="space-y-2 overflow-auto">
            {posts.map(post => (
              <PostCard
                key={post.id}
                id={post.id}
                title={post.title}
                message={post.message}
                createdAt={post.createdAt}
                publishedAt={post.publishedAt}
                deal={post.deal}
                sender={post.sender}
                syndicate={post.syndicate}
                onClick={() => setPostOnDisplay(post.id)}
                status={post.status}
              />
            ))}
          </div>
          {pageInfo.hasNextPage ? (
            <div className="flex justify-center p-4">
              <Button
                loading={loading}
                onClick={() =>
                  fetchMore({
                    variables: {
                      cursor: pageInfo.cursor,
                    },
                  })
                }
              >
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Syndicate Posts"
        subTitle="Broadcast updates and information to your network"
      />
      <SidebarLayoutContent>
        <Alert
          type="info"
          title="Use this for global updates and information to your network only"
          message='For specific deal updates, use the "Posts" tab in the deal page'
        />
        <CreatePostDialogButton />
        {renderContent()}
      </SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default SyndicatePosts;
